/* Wizard · Step 2 · Clinical Exam
 * Extracted iter101.91 from `/app/frontend/src/pages/doctor/CaseSubmissionPage.jsx`.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";
import {
    CaretRight, CaretLeft, CaretDown, CheckCircle, Circle, Upload, X, Check,
    MagnifyingGlass, ArrowsCounterClockwise, Crown, FileArrowUp, GitBranch,
    Warning, Lightning, Clock, Info, Tooth, Star, ShieldCheck, Cube, Cloud,
    CloudArrowUp, File as FileIcon, FilePdf, FileZip, FileVideo, Sparkle, Tag,
    ClipboardText, HeartStraight, Ruler, TrendUp, Sliders, Package, Prohibit,
    Notepad, ListBullets, Pencil, ArrowSquareUp, ArrowSquareDown, CircleHalf,
    Printer, BookmarkSimple, FloppyDisk, Trash, ChartBar, Hourglass, Calculator,
    Lock, UserGear, Camera, Stethoscope, HouseLine, Plus, Prescription,
    Headset, Truck, Receipt, SealCheck, Buildings, Hash, Scan, ArrowRight,
    User as UserIcon,
} from "@phosphor-icons/react";
import PerToothPlanAccordion from "@/components/case/PerToothPlanAccordion";
import DetailedClinicalExam, { ExamAccordion, present } from "@/components/case/DetailedClinicalExam";
import TreatmentObjectives from "@/components/case/TreatmentObjectives";
import CaseSubmittedDialog from "@/components/case/CaseSubmittedDialog";
import PlanTimelineSection, { RetentionRecallPair } from "@/components/case/PlanTimelineSection";
import AdvancePaymentBlock from "@/components/case/AdvancePaymentBlock";
import RequestScanVisitModal from "@/components/patient/RequestScanVisitModal";
import PatientAvatar from "@/components/patient/PatientAvatar";
import AddPatientDialog from "@/components/doctor/AddPatientDialog";
import DemoFillButton from "@/components/case-wizard/DemoFillButton";
import PrescriptionComplianceBlock from "@/components/case/PrescriptionComplianceBlock";
import WizardPaymentCommitment from "@/components/case/WizardPaymentCommitment";
import InvisiqueLogo from "@/components/site/Logo";
import {
    LabSection, LabChip, LabChoiceCard, LabInfoNote, LabGlanceRow,
    FdiChipPicker, PhaseSplitVisualizer,
} from "@/components/case/wizard/lab-section";
import {
    SelfPlanLabInstructions, StepLabInstructions, TierInclusionRow,
} from "@/components/case/wizard/steps/StepLabInstructions";
import {
    DraftSavedChip, ChildCaseBanner, StepIndicator, WizardStepHero,
    Step1SectionHeader, Step1ProgressSpine, Step1AnsweredStack,
    Step1AnsweredStrip, Step1ActiveWrap, Step1RecapBanner, RecapChip,
    ReviewBlock, Row, SelectField, SelectedPill,
    cardBadgeClass, resolveHeroIcon, fmtINR,
} from "@/components/case/wizard/shared";
import {
    ScanSourcePicker, RadioRailRow, CoeSupportCompliancePicker,
    PlanningModePicker, ModeCard,
} from "@/components/case/wizard/pickers";
import {
    IOScannerToggleCard, TierPicker, TierFeature, LabVisitScanFeeRow,
    PromoBlock, PricingAndOffersCard, PrescriptionPreviewCard,
    PreviewBlock, PreviewRow, CoeOrderPreviewCard, PriorityPicker,
    SlotUploader, PlanningSoftwareExportCard, CoeHandoffCallout,
} from "@/components/case/wizard/blocks";


export function StepClinical({ clinical, setClinical, metadata, diagnosis, setDiagnosis, treatmentNotes, setTreatmentNotes, planningMode }) {
    const upd = (k, v) => setClinical((c) => ({ ...c, [k]: v }));
    // iter84 — sanitise number inputs. Empty string stays empty so
    // optional fields don't get coerced to 0; otherwise clamp to 1
    // decimal place so the planner sees clean values.
    const updNum = (k, raw) => {
        if (raw === "" || raw == null) { upd(k, ""); return; }
        const n = Number(raw);
        if (Number.isFinite(n)) upd(k, n);
    };
    const prevOrtho = clinical.previous_ortho || "";
    const showPrevOrthoNotes = prevOrtho && prevOrtho !== "none";

    /* iter97.66 — Per-section fill counts (used by both the accordion
       badges and the page-level progress strip below). Listed up here
       so the accordion calls stay terse. After the redundancy cleanup
       (iter97.66) the per-section totals are:
         Diagnosis (3): diagnosis, chief_complaint, treatmentNotes
         Treatment scope (3): arch, extraction_plan, extraction_teeth
         Medical history (3): medical_history, missing_teeth, previous_ortho
         Bite measurements (2): overbite_mm, overjet_mm
         Occlusal (6): angle_class_(R/L/Ant), skeletal, vertical, midline
         Crowding (4): upper, lower, distribution, spacing_location
         Posterior (4): buccal_(R/L), canine_(R/L)
         Functional (4): anterior_guidance, lateral_(R/L), protrusive
         Periodontal (3): periodontal_health, plaque_index, perio_compliance
       Total = 32 fields across 9 accordions. */
    const counts = {
        diagnosis: [diagnosis, clinical.chief_complaint, treatmentNotes].filter(present).length,
        scope: [clinical.arch, clinical.extraction_plan, clinical.extraction_teeth].filter(present).length,
        history: [clinical.medical_history, clinical.missing_teeth, clinical.previous_ortho].filter(present).length,
        bite: [clinical.overbite_mm, clinical.overjet_mm].filter(present).length,
        occlusal: [
            clinical.angle_class_right, clinical.angle_class_left, clinical.angle_class_anterior,
            clinical.skeletal_class, clinical.vertical_dimension, clinical.midline_relationship,
        ].filter(present).length,
        crowding: [
            clinical.upper_arch_crowding_mm, clinical.lower_arch_crowding_mm,
            clinical.crowding_distribution, clinical.spacing_location,
        ].filter(present).length,
        posterior: [
            clinical.buccal_occlusion_right, clinical.buccal_occlusion_left,
            clinical.canine_class_right, clinical.canine_class_left,
        ].filter(present).length,
        functional: [
            clinical.anterior_guidance, clinical.lateral_movement_right,
            clinical.lateral_movement_left, clinical.protrusive_movement,
        ].filter(present).length,
        perio: [clinical.periodontal_health, clinical.plaque_index, clinical.perio_compliance].filter(present).length,
    };
    const totals = { diagnosis: 3, scope: 3, history: 3, bite: 2, occlusal: 6, crowding: 4, posterior: 4, functional: 4, perio: 3 };
    const totalFilled = Object.values(counts).reduce((s, n) => s + n, 0);
    const totalFields = Object.values(totals).reduce((s, n) => s + n, 0);
    const progressPct = Math.round((totalFilled / totalFields) * 100);

    /* iter97.67 — Case overview merges the 4 small primary accordions
       (Diagnosis, Scope, Medical history, Bite measurements) into one
       so the doctor isn't expanding a row just to fill 2-3 fields. The
       4 ideas remain visually separated inside via dashed dividers. */
    const overviewFilled = counts.diagnosis + counts.scope + counts.history + counts.bite;
    const overviewTotal = totals.diagnosis + totals.scope + totals.history + totals.bite; // 11

    /* Six top-level sections after the merge: 1 overview + 5 detailed
       findings. The progress strip badge counts these. */
    const DETAILED_IDS = ["occlusal", "crowding", "posterior", "functional", "perio"];
    const sectionsComplete =
        (overviewFilled >= overviewTotal ? 1 : 0) +
        DETAILED_IDS.filter((k) => counts[k] >= totals[k]).length;
    const TOTAL_SECTIONS = 6;

    /* iter97.67 — Single-open accordion state for the entire Step 2.
       Defaults to "overview" so the doctor lands on the basics. Each
       "Next →" button advances to the next id and smooth-scrolls the
       target into view, so the user always knows what's next and
       doesn't accidentally hit the sticky step Next button. */
    /* iter97.86 — All accordions start CLOSED. Doctor opens the section
       they want to edit; opening another auto-closes the previous one
       (single-open semantics). Was `"overview"` (auto-opened on entry)
       which conflicted with the platform-wide "start closed" rule. */
    const [openId, setOpenId] = useState(null);
    const goNext = (nextId) => {
        setOpenId(nextId);
        /* iter97.68 — Explicit window.scrollTo with a measured 180px
           offset to clear the dual sticky chrome (65px brand header +
           ~100px wizard title/stepper). Computed AFTER React re-render
           via double-buffered requestAnimationFrame so the page-shrink
           from the closing accordion + the page-grow from the opening
           accordion are both committed before we read the target's
           coordinates. */
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const el = document.querySelector(`[data-testid="exam-acc-${nextId}"]`);
                if (!el) return;
                const STICKY_OFFSET = 180;
                const elTop = el.getBoundingClientRect().top + window.pageYOffset;
                window.scrollTo({ top: Math.max(0, elTop - STICKY_OFFSET), behavior: "smooth" });
            });
        });
    };

    /* iter97.66 — "Apply common defaults" — one-click prefill for the
       classic Class I mild-crowding presentation. Only fills empty
       fields (idempotent for partial users). Wins for the doctor: gets
       past 6 cells in one tap; only edits the cells that differ. */
    const applyCommonDefaults = () => {
        setClinical((c) => ({
            ...c,
            angle_class_right: c.angle_class_right || "class_i",
            angle_class_left: c.angle_class_left || "class_i",
            angle_class_anterior: c.angle_class_anterior || "class_i",
            skeletal_class: c.skeletal_class || "class_i",
            buccal_occlusion_right: c.buccal_occlusion_right || "class_i",
            buccal_occlusion_left: c.buccal_occlusion_left || "class_i",
            canine_class_right: c.canine_class_right || "class_i",
            canine_class_left: c.canine_class_left || "class_i",
            upper_arch_crowding_mm: (c.upper_arch_crowding_mm === "" || c.upper_arch_crowding_mm == null) ? 1.0 : c.upper_arch_crowding_mm,
            lower_arch_crowding_mm: (c.lower_arch_crowding_mm === "" || c.lower_arch_crowding_mm == null) ? 1.0 : c.lower_arch_crowding_mm,
        }));
    };

    return (
        /* iter97.68 — `pb-[60vh]` adds ~650px of bottom breathing room
           so the document is always taller than the viewport. Without
           this, opening a mid-or-late accordion can't scroll it to
           ~180px below the sticky chrome because the browser caps at
           the document's max scroll. */
        <div className="space-y-2.5 pb-[60vh]">
            {/* iter97.67 — Merged "Case overview" accordion. Houses the
                4 small sections (Diagnosis, Treatment scope, Medical
                history, Bite measurements) so the doctor doesn't open
                a row just to fill 2-3 fields. Visually divided inside
                with dashed separators + tinted section headers. */}
            <ExamAccordion
                id="overview"
                title="Case overview"
                sub="Diagnosis · Treatment scope · Medical history · Bite measurements"
                icon={<ClipboardText size={17} weight="duotone" />}
                tone="slate"
                open={openId === "overview"}
                onOpenChange={() => setOpenId((cur) => (cur === "overview" ? null : "overview"))}
                nextLabel="Occlusal classification"
                onNext={() => goNext("occlusal")}
                filled={overviewFilled}
                total={overviewTotal}
            >
                <div className="space-y-5">
                    {/* §1 — Diagnosis & treatment notes (3 fields) */}
                    <div>
                        <div className="text-[10.5px] font-bold uppercase tracking-[0.15em] text-slate-600 mb-2.5 flex items-center gap-1.5">
                            <ClipboardText size={12} weight="bold" />
                            Diagnosis & treatment notes
                            <span className="ml-auto text-[10px] font-medium text-muted-foreground normal-case tracking-normal">
                                {counts.diagnosis}/{totals.diagnosis}
                            </span>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <Label className="text-xs font-semibold">Diagnosis / Chief reason</Label>
                                <Input
                                    value={diagnosis}
                                    onChange={(e) => setDiagnosis(e.target.value)}
                                    placeholder="e.g. Class II div 1, mild crowding"
                                    className="h-10 mt-1"
                                    data-testid="case-wizard-diagnosis"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Patient's chief complaint</Label>
                                <Textarea
                                    rows={2}
                                    value={clinical.chief_complaint || ""}
                                    onChange={(e) => upd("chief_complaint", e.target.value)}
                                    placeholder="In the patient's own words — e.g. 'I don't like my front teeth sticking out.'"
                                    className="mt-1"
                                    data-testid="case-wizard-chief-complaint"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Treatment notes & special considerations</Label>
                                <Textarea
                                    rows={3}
                                    value={treatmentNotes}
                                    onChange={(e) => setTreatmentNotes(e.target.value)}
                                    placeholder="Movement priorities, expectations, midlines, facial profile, any constraints…"
                                    className="mt-1"
                                    data-testid="case-wizard-treatment-notes"
                                />
                            </div>
                        </div>
                    </div>

                    {/* §2 — Treatment scope (3 fields) */}
                    <div className="pt-4 border-t border-dashed border-border/70">
                        <div className="text-[10.5px] font-bold uppercase tracking-[0.15em] text-teal-700 mb-2.5 flex items-center gap-1.5">
                            <Tooth size={12} weight="bold" />
                            Treatment scope
                            <span className="ml-auto text-[10px] font-medium text-muted-foreground normal-case tracking-normal">
                                {counts.scope}/{totals.scope}
                            </span>
                        </div>
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <SelectField label="Arch involvement" value={clinical.arch} onChange={(v) => upd("arch", v)} options={metadata.arches} testid="case-wizard-arch" />
                                <SelectField label="Extraction plan" value={clinical.extraction_plan} onChange={(v) => upd("extraction_plan", v)} options={metadata.extraction_plans} testid="case-wizard-extraction" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Extraction teeth (if planned/done)</Label>
                                <Input value={clinical.extraction_teeth} onChange={(e) => upd("extraction_teeth", e.target.value)} placeholder="e.g. 14, 24" className="h-10 mt-1" />
                            </div>
                        </div>
                    </div>

                    {/* §3 — Medical & ortho history (3 fields) */}
                    <div className="pt-4 border-t border-dashed border-border/70">
                        <div className="text-[10.5px] font-bold uppercase tracking-[0.15em] text-rose-700 mb-2.5 flex items-center gap-1.5">
                            <HeartStraight size={12} weight="bold" />
                            Medical & ortho history
                            <span className="ml-auto text-[10px] font-medium text-muted-foreground normal-case tracking-normal">
                                {counts.history}/{totals.history}
                            </span>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <Label className="text-xs font-semibold">Medical history & allergies</Label>
                                <Textarea
                                    rows={2}
                                    value={clinical.medical_history || ""}
                                    onChange={(e) => upd("medical_history", e.target.value)}
                                    placeholder="e.g. Latex allergy, controlled hypertension, GERD, bruxism…"
                                    className="mt-1"
                                    data-testid="case-wizard-medical-history"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Missing teeth</Label>
                                <Input
                                    value={clinical.missing_teeth || ""}
                                    onChange={(e) => upd("missing_teeth", e.target.value)}
                                    placeholder="FDI notation, e.g. 18, 28, 38, 48"
                                    className="h-10 mt-1"
                                    data-testid="case-wizard-missing-teeth"
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <SelectField
                                    label="Previous ortho treatment"
                                    value={prevOrtho}
                                    onChange={(v) => upd("previous_ortho", v)}
                                    options={metadata.previous_ortho_options || ["none", "braces", "aligners", "myofunctional", "other"]}
                                    testid="case-wizard-previous-ortho"
                                />
                                {showPrevOrthoNotes && (
                                    <div>
                                        <Label className="text-xs font-semibold">When / outcome</Label>
                                        <Input
                                            value={clinical.previous_ortho_notes || ""}
                                            onChange={(e) => upd("previous_ortho_notes", e.target.value)}
                                            placeholder="e.g. Braces 2014–16, lower relapse"
                                            className="h-10 mt-1"
                                            data-testid="case-wizard-previous-ortho-notes"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* §4 — Bite measurements (2 fields) */}
                    <div className="pt-4 border-t border-dashed border-border/70">
                        <div className="text-[10.5px] font-bold uppercase tracking-[0.15em] text-violet-700 mb-2.5 flex items-center gap-1.5">
                            <Ruler size={12} weight="bold" />
                            Bite measurements (chairside)
                            <span className="ml-auto text-[10px] font-medium text-muted-foreground normal-case tracking-normal">
                                {counts.bite}/{totals.bite}
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold">Overbite (mm)</Label>
                                <Input
                                    type="number"
                                    step="0.1"
                                    value={clinical.overbite_mm ?? ""}
                                    onChange={(e) => updNum("overbite_mm", e.target.value)}
                                    placeholder="e.g. 3.5"
                                    className="h-10 mt-1"
                                    data-testid="case-wizard-overbite"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Overjet (mm)</Label>
                                <Input
                                    type="number"
                                    step="0.1"
                                    value={clinical.overjet_mm ?? ""}
                                    onChange={(e) => updNum("overjet_mm", e.target.value)}
                                    placeholder="e.g. 2.0"
                                    className="h-10 mt-1"
                                    data-testid="case-wizard-overjet"
                                />
                            </div>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1.5">
                            Optional — planner verifies on the STL. Negative overjet = anterior crossbite.
                        </p>
                    </div>
                </div>
            </ExamAccordion>

            {/* iter97.63 — Detailed findings (5 sub-accordions). Renders
                inline so Step 2 becomes one continuous list of 6 accordions:
                1 merged overview + 5 detailed, all in the same rhythm.
                iter97.67 — Now driven by the same openId state so the
                whole step behaves as a single-open accordion with
                guided Next → flow. */}
            <DetailedClinicalExam
                clinical={clinical}
                setClinical={setClinical}
                metadata={metadata}
                planningMode={planningMode}
                openId={openId}
                setOpenId={setOpenId}
                goNext={goNext}
            />
        </div>
    );
}

/* ──────────── iter97.50 — Invisique IO Scanning Service toggle ────────────
   Appears in both Step 1 (Case Setup) and Step 5 (Attachments/Scans).
   Both instances drive the same `pricing.scanner_pickup_requested`
   field, so the doctor's choice is mirrored across the wizard.
   The fee is admin-configurable; we read it once via /settings/io-scanner-fee
   and pass it down here as a prop so the card always shows the live rate. */
